import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  TextField,
  IconButton,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import { format, isToday, isYesterday, isSameDay } from "date-fns";
import Header from "../../components/Header";
import {
  useGetSupportInboxQuery,
  useGetSupportHistoryQuery,
  useSendSupportMessageMutation,
} from "../../state/api";

const dayLabel = (date) => {
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "MMMM d, yyyy");
};

const mediaUrl = (path) => {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  return `${axios.defaults.baseURL}/${path}`;
};

const SupportChat = () => {
  const theme = useTheme();
  const { data: inboxData, isLoading: inboxLoading } = useGetSupportInboxQuery(
    undefined,
    { pollingInterval: 5000 }
  );
  const [selectedUserId, setSelectedUserId] = useState(null);
  const { data: historyData, isLoading: historyLoading } = useGetSupportHistoryQuery(
    selectedUserId,
    { skip: !selectedUserId, pollingInterval: 4000 }
  );
  const [sendMessage] = useSendSupportMessageMutation();
  const [draft, setDraft] = useState("");
  const bottomRef = useRef(null);

  const threads = (inboxData && inboxData.data) || [];
  const messages = (historyData && historyData.data) || [];
  const selectedThread = threads.find((t) => t.userId === selectedUserId);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleSend = async () => {
    if (!draft.trim() || !selectedUserId) return;
    try {
      await sendMessage({ userId: selectedUserId, message: draft.trim() }).unwrap();
      setDraft("");
    } catch (error) {
      console.error("Error sending support reply", error);
    }
  };

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="SUPPORT CHAT" />
      <Box
        mt="20px"
        height="75vh"
        display="flex"
        borderRadius="0.75rem"
        overflow="hidden"
        sx={{ border: `1px solid ${theme.palette.secondary[700] || "#333"}` }}
      >
        {/* Inbox list */}
        <Box
          width="320px"
          flexShrink={0}
          sx={{ backgroundColor: theme.palette.background.alt, overflowY: "auto" }}
        >
          {inboxLoading && (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!inboxLoading && threads.length === 0 && (
            <Typography sx={{ p: 2, color: theme.palette.secondary[200] }}>
              No support messages yet.
            </Typography>
          )}
          <List disablePadding>
            {threads.map((t) => (
              <ListItemButton
                key={t.userId}
                selected={t.userId === selectedUserId}
                onClick={() => setSelectedUserId(t.userId)}
                sx={{ borderBottom: `1px solid ${theme.palette.background.default}` }}
              >
                <ListItemAvatar>
                  <Badge
                    color="error"
                    badgeContent={t.unreadCount}
                    invisible={!t.unreadCount}
                  >
                    <Avatar src={mediaUrl(t.profilePicture)}>
                      {t.fullname ? t.fullname.charAt(0).toUpperCase() : "?"}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography component="span" noWrap sx={{ fontWeight: 500 }}>
                        {t.fullname || t.username}
                      </Typography>
                      {t.lastMessageAt && (
                        <Typography
                          component="span"
                          sx={{ fontSize: "0.65rem", opacity: 0.6, ml: 1, flexShrink: 0 }}
                        >
                          {isToday(new Date(t.lastMessageAt))
                            ? format(new Date(t.lastMessageAt), "h:mm a")
                            : format(new Date(t.lastMessageAt), "MMM d")}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    (t.lastSenderRole === "admin" ? "You: " : "") +
                    (t.lastMessage || "")
                  }
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Conversation */}
        <Box flex={1} display="flex" flexDirection="column">
          {!selectedUserId ? (
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography color={theme.palette.secondary[200]}>
                Select a conversation to view messages
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                p={2}
                sx={{
                  backgroundColor: theme.palette.background.alt,
                  borderBottom: `1px solid ${theme.palette.background.default}`,
                }}
              >
                <Typography variant="h5">
                  {selectedThread?.fullname || selectedThread?.username}
                </Typography>
              </Box>
              <Box flex={1} overflow="auto" p={2}>
                {historyLoading && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <CircularProgress size={24} />
                  </Box>
                )}
                {messages.map((m, index) => {
                  const createdAt = new Date(m.createdAt);
                  const prevCreatedAt =
                    index > 0 ? new Date(messages[index - 1].createdAt) : null;
                  const showDivider =
                    !prevCreatedAt || !isSameDay(createdAt, prevCreatedAt);

                  return (
                    <React.Fragment key={m._id}>
                      {showDivider && (
                        <Box display="flex" justifyContent="center" my={2}>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              px: 1.5,
                              py: 0.3,
                              borderRadius: "10px",
                              backgroundColor: theme.palette.background.default,
                              color: theme.palette.secondary[200],
                            }}
                          >
                            {dayLabel(createdAt)}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        display="flex"
                        justifyContent={m.senderRole === "admin" ? "flex-end" : "flex-start"}
                        mb={1.5}
                      >
                        <Box
                          maxWidth="60%"
                          px={2}
                          py={1}
                          borderRadius="10px"
                          sx={{
                            // secondary.main resolves to a dark maroon in
                            // this theme, not a bright accent — paired with
                            // background.default (near-black) text that was
                            // dark-on-dark and unreadable. secondary[400] is
                            // the bright gold already used elsewhere (KYC
                            // badges, ratings) for a visible highlight.
                            backgroundColor:
                              m.senderRole === "admin"
                                ? theme.palette.secondary[400]
                                : theme.palette.background.alt,
                            color:
                              m.senderRole === "admin"
                                ? "#1a1a1a"
                                : theme.palette.secondary[100],
                          }}
                        >
                          <Typography sx={{ fontSize: "0.7rem", opacity: 0.7, mb: 0.3 }}>
                            {m.senderName}
                          </Typography>
                          <Typography sx={{ wordBreak: "break-word" }}>
                            {m.message}
                          </Typography>
                          <Typography sx={{ fontSize: "0.65rem", opacity: 0.6, mt: 0.3 }}>
                            {format(createdAt, "h:mm a")}
                          </Typography>
                        </Box>
                      </Box>
                    </React.Fragment>
                  );
                })}
                <div ref={bottomRef} />
              </Box>
              <Box
                p={2}
                display="flex"
                gap={1}
                sx={{
                  backgroundColor: theme.palette.background.alt,
                  borderTop: `1px solid ${theme.palette.background.default}`,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Type a reply..."
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
                <IconButton color="primary" onClick={handleSend} disabled={!draft.trim()}>
                  <SendIcon />
                </IconButton>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SupportChat;
